import { useEffect, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../axiosInstance';
import { API_URL_All } from '../api';
import * as general from '../libs/general';
import DataTable from '../components/ui/DataTable';
import Paging from '../components/Paging';
import TthReconciliationReview from '../components/orders/TthReconciliationReview';

/**
 * Tab "Tạo đối soát TTH" — đơn đủ điều kiện: statusId="Thành công" và statusRateId="Chưa đối soát"
 * (mục 8.1 BRD). Chọn đơn của CÙNG 1 đối tác rồi "Tạo đối soát" (mục 8.2 BRD). Layout/lib-filter
 * (ô "Mã đối tác" autocomplete kiểu filter-editor) mirror NewReconciliation.jsx (luồng Normal).
 */
function NewTthReconciliation() {
  const [pageData, setPageData] = useState({ content: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isChecking, setIsChecking] = useState(false);
  const [checkingReviewData, setCheckingReviewData] = useState(null);
  const appliedPartnerRef = useRef('');

  const search = async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const params = {};
      if (appliedPartnerRef.current) params.partnerCode = appliedPartnerRef.current;
      const res = await axiosInstance.get('/api/tth-invoices/searchEligibleTthOrders', { params });
      setPageData(res.data);
      setSelectedIds([]);
      // .check_all chỉ hiện khi có data-listids (xem zto.css:239 — mirror cơ chế NewReconciliation.jsx
      // dùng cho luồng Normal) — không set thì checkbox "chọn tất cả" bị display:none vĩnh viễn.
      const checkAllTag = document.querySelector('.check_all');
      if (checkAllTag) {
        checkAllTag.setAttribute('data-listids', (res.data.listIds || []).join(','));
      }
    } catch (err) {
      setErrorMessage(err.message);
      setPageData({ content: [] });
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  useEffect(() => { search(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleSearchClick = () => {
    const formData = general.getFormData('#tth_reconcile_filter');
    appliedPartnerRef.current = (formData.partnerCode || '').trim();
    setIsSearching(true);
    search();
  };

  const content = pageData.page?.content || [];

  const allSelected = content.length > 0 && content.every((o) => selectedIds.includes(o.id));
  const handleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? content.map((o) => o.id) : []);
  };
  const handleSelectOrder = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const prepareChecking = async () => {
    if (selectedIds.length === 0) {
      toast.error('Phải chọn ít nhất một đơn.');
      return;
    }
    setIsChecking(true);
    try {
      const res = await axiosInstance.post('/api/tth-invoices/getTthInvoiceReview', { codes: selectedIds.join(';') },
        { headers: { 'Content-Type': 'application/json' } });
      if (res.data?.success === false) {
        toast.error(res.data.message);
        return;
      }
      setCheckingReviewData({ listIds: selectedIds });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="table-responsive web-table">
      <div className="container-fluid pt-2">
        <div className="text-start mb-2 normal-form">
          <div className="row">
            <div className="col-md-8">
              <form id="tth_reconcile_filter">
                <div className="row">
                  <div className="col-md-7 height-46">
                    <div className="row">
                      <label className="form-label col-form-label col-md-4 text-end">Mã đối tác</label>
                      <div className="col-md-8">
                        <div className="th th-col filter-editor">
                          {/* Chỉ gợi ý đối tác đã được gán Line "Line TTH" (xem Contact.jsx — gán Line
                              cho đối tác), dùng đúng cơ chế lọc theo Line (searchContactNames +
                              data-refer) mà TthOrderEditor.jsx đang dùng, thay vì searchContactCodes
                              vốn trả về TẤT CẢ đối tác. */}
                          <span className="filter-ib" data-source-key="code" data-source-url={API_URL_All + '/api/contacts/searchContactNames'}>
                            <input
                              type="text"
                              name="partnerCode"
                              placeholder="Nhập mã đối tác..."
                              autoComplete="off"
                              data-refer="Line TTH"
                              defaultValue={appliedPartnerRef.current}
                            />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2 p-0">
                    <button
                      onClick={handleSearchClick}
                      className="btn btn-light btn-sm d-inline-flex align-items-center justify-content-center zto-btn-icon-sm"
                      type="button"
                      title="Tìm kiếm..."
                      disabled={isSearching}
                      aria-busy={isSearching}
                    >
                      {isSearching ? <span className="spinner-border spinner-border-sm" aria-hidden="true"></span> : <FaSearch />}
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <div className="col-md-3">
              <button
                onClick={prepareChecking}
                type="button"
                className="btn btn-info btn-sm me-2 d-inline-flex align-items-center justify-content-center gap-1 zto-btn-min-122"
                disabled={isChecking || isSearching}
                aria-busy={isChecking}
              >
                {isChecking ? (
                  <>
                    <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                    Đang xử lý
                  </>
                ) : (
                  'Tạo đối soát'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <section className="p-1">
        <DataTable maxHeight="calc(100vh - 220px)" className="table-normal" data-loading={isLoading}>
          <table className="table table-striped table-bordered border-normal">
            <thead className="table-primary">
              <tr>
                <th className="sticky" width="30px">
                  <input type="checkbox" title="Check all" className="check_all" onChange={handleSelectAll}
                    checked={allSelected} />
                </th>
                <th className="text-center"><span>Mã đơn</span></th>
                <th className="text-center"><span>Ngày tạo</span></th>
                <th className="text-center"><span>Mã KH</span></th>
                <th className="text-center"><span>Mã Đối tác</span></th>
                <th className="text-center"><span>Số lượng Tệ</span></th>
                <th className="text-center"><span>Chênh lệch</span></th>
              </tr>
            </thead>
            <tbody className="tbody-row">
              {content.map((item) => (
                <tr key={item.id}>
                  <td className="sticky">
                    <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => handleSelectOrder(item.id)} />
                  </td>
                  <td className="text-success text-center">{item.orderCode}</td>
                  <td className="text-success text-center" title={general.formatTimeShort(item.createdDate)}>{general.formatDate(item.createdDate)}</td>
                  <td className="text-success text-center">{item.customerCode}</td>
                  <td className="text-success text-center">{item.partnerCode}</td>
                  <td className="text-success text-center">{general.formatDecimal(item.quantity)}</td>
                  <td className="text-success text-center">{general.formatNumber(item.amountDiff)}</td>
                </tr>
              ))}
              {!isLoading && errorMessage === '' && content.length === 0 && (
                <tr><td colSpan="7" className="text-center">Không có đơn nào đủ điều kiện đối soát</td></tr>
              )}
              {errorMessage !== '' && (
                <tr><td colSpan="7" className="text-center">{errorMessage}</td></tr>
              )}
            </tbody>
          </table>
        </DataTable>
      </section>

      <div className="my-1 d-flex justify-content-end align-items-center">
        <Paging pageData={pageData} onPageChange={() => {}} onItemsPerPageChange={() => {}} hidePaginationWhenSinglePage isShowTotal />
      </div>

      {checkingReviewData && (
        <TthReconciliationReview data={checkingReviewData} handleSearch={search} canEdit={true} />
      )}
    </div>
  );
}

export default NewTthReconciliation;
